import React, { useRef, useState, useEffect, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next'
import VideoContext from './VideoContext'
//@ts-ignore
import AiNumberReader from './AiNumberReader.worker'

interface VideoProviderProps {
  children: ReactNode;
}

// window.BarcodeDetector isn't reliable on Safari - a polyfill loaded elsewhere in the app
// can install it too, so we can't just feature-detect.
function isSafariBrowser(): boolean {
  return (window.navigator.vendor || '').includes('Apple')
}

const VideoProvider: React.FC<VideoProviderProps> = ({ children }) => {

  const { t } = useTranslation()

  const [cameraState, setCameraState] = useState<string>('IDLE')
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null)
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([])
  const [QRCodesPresent, setQRCodesPresent] = useState<any>(null)
  const [QRCodeSearchOptions, setQRCodesSearchOptions] = useState<any>(null)
  const [isFacingUser, setIsFacingUser] = useState<boolean>(false)
  const [zoom, setZoom] = useState<number>(1)
  const [longExposureMode, setLongExposureMode] = useState<boolean>(false)

  const animationFrameHandleRef = useRef<number>(0) // this is needed for cancel
  const canvasesToStreamToRef = useRef<React.RefObject<HTMLCanvasElement>[]>([])


  const mediaStreamRef = useRef<MediaStream | null>(null)
  const frameSinceQRCodeSpotted = useRef<number>(0)
  const QRCodeSearchOptionsRef = useRef<any>(null)
  const videoStartLockRef = useRef<string | null>(null)
  const cameraStateRef = useRef<string | null>(null)

  const webWorkerRef = useRef<any>(null)

  const videoElementRef = useRef<HTMLVideoElement>(null)
  const rawVideoCanvasRef = useRef<HTMLCanvasElement>(null)
  const captureCanvasRef = useRef<HTMLCanvasElement>(null)
  const rotatedCanvasRef = useRef<HTMLCanvasElement>(null)


  const barcodeModeRef = useRef<string>(null)
  const barcodeDetectorRef = useRef<any>(null)
  const isFacingUserRef = useRef<any>(null)
  const tickZoom = useRef<number>(1)
  const tickLongExposureMode = useRef<boolean>(true)


  useEffect(() => { QRCodeSearchOptionsRef.current = QRCodeSearchOptions }, [QRCodeSearchOptions])
  useEffect(() => { isFacingUserRef.current = isFacingUser }, [isFacingUser])
  useEffect(() => { cameraStateRef.current = cameraState }, [cameraState])
  useEffect(() => { tickZoom.current = zoom }, [zoom])
  useEffect(() => { tickLongExposureMode.current = longExposureMode }, [longExposureMode])

  useEffect(() => {
    if (webWorkerRef.current === null) {
      //console.log("Creating new web worker")
      const worker = new AiNumberReader()
      webWorkerRef.current = worker
      worker.onmessage = handleMessageFromWebWorker
      if ("BarcodeDetector" in window && !isSafariBrowser()) {
        //@ts-ignore
        barcodeDetectorRef.current = new window.BarcodeDetector({
          formats: ["qr_code"]
        });
        //@ts-ignore
        barcodeModeRef.current = "browser"
      } else {
        //@ts-ignore
        barcodeModeRef.current = "jsqr"
      }

    }

  }, [])

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        if (cameraStateRef.current === 'RUNNING') {
          stopCamera()
          setCameraState('PAUSED_TAB_INACTIVE')
          cameraStateRef.current = 'PAUSED_TAB_INACTIVE'
        }
        //console.log('Tab is inactive.');
      } else {
        //console.log('Tab is active.');
        if (cameraStateRef.current === 'PAUSED_TAB_INACTIVE') {
          startCamera()
          setCameraState('RUNNING')
          cameraStateRef.current = 'RUNNING'
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return (() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    })

  }, [])



  useEffect(() => {
    const updateAvailableCameras = async () => {
      const videoDevices = await _getAvailableDevices()
      setAvailableCameras(videoDevices)
    }
    updateAvailableCameras()
    // some android phones (e.g. our Hauwei) change their mind about what cameras they have a short time after we first ask
    setTimeout(updateAvailableCameras, 1000)
    if (navigator && navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = updateAvailableCameras
      return (() => {
        navigator.mediaDevices.removeEventListener('devicechange', updateAvailableCameras);
      })
    }
  }, [])



  const tick = useCallback((frameCount: number) => {
    const videoElement = videoElementRef.current!
    const beta = 0.2

    let newFrameCount = frameCount
    let isVideoPlayingRightNow = false
    if (canvasesToStreamToRef.current.length > 0) {
      newFrameCount = frameCount + 1
      isVideoPlayingRightNow = true
    }

    if (isVideoPlayingRightNow && videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
      const video_width = videoElement.videoWidth
      const video_height = videoElement.videoHeight

      const rawVideoCanvas = rawVideoCanvasRef.current
      const raw_ctx = rawVideoCanvas!.getContext("2d")
      const qr_search_options = QRCodeSearchOptionsRef.current || {}
      const look_for_qr_codes = qr_search_options && qr_search_options['enabled']

      if (look_for_qr_codes) {
        // scaled video height should be no more than 480x480
        const max_dimension = 2160
        let scale_factor = 1
        if (video_width > video_height) {
          if (video_width > max_dimension) {
            scale_factor = max_dimension / video_width
          }
        } else {
          if (video_height > max_dimension) {
            scale_factor = max_dimension / video_height
          }
        }

        const scaled_video_width = Math.ceil(video_width * scale_factor)
        const scaled_video_height = Math.ceil(video_height * scale_factor)

        if (rawVideoCanvas!.width !== scaled_video_width || rawVideoCanvas!.height !== scaled_video_height) {
          rawVideoCanvas!.width = scaled_video_width
          rawVideoCanvas!.height = scaled_video_height
        }

        const searchArea = {
          left: 0,
          top: 0,
          width: scaled_video_width,
          height: scaled_video_height
        }

        if (qr_search_options && qr_search_options['searchArea']) {
          const scaleFactors = qr_search_options['searchArea']
          searchArea.top = searchArea.height * scaleFactors.top
          searchArea.left = searchArea.width * scaleFactors.left
          searchArea.width = searchArea.width * scaleFactors.width
          searchArea.height = searchArea.height * scaleFactors.height
        }


        // This is the video that gets snapshotted as the photo

        // apply scaling to raw video, keeping it centred
        raw_ctx!.save()
        raw_ctx!.translate(rawVideoCanvas!.width / 2, rawVideoCanvas!.height / 2);
        raw_ctx!.scale(tickZoom.current, tickZoom.current)
        raw_ctx!.translate(-rawVideoCanvas!.width / 2, -rawVideoCanvas!.height / 2);

        if (tickLongExposureMode.current) {
          raw_ctx!.globalCompositeOperation = "source-over";
          raw_ctx!.globalAlpha = beta;
          raw_ctx!.drawImage(videoElement, 0, 0, rawVideoCanvas!.width, rawVideoCanvas!.height)
        } else {

          raw_ctx!.drawImage(videoElement, 0, 0, rawVideoCanvas!.width, rawVideoCanvas!.height)

        }

        raw_ctx!.restore()

        if (raw_ctx) {
          const rawVideoImageData = raw_ctx!.getImageData(searchArea.left, searchArea.top, searchArea.width, searchArea.height)

          if (rawVideoImageData) {
            if (barcodeModeRef.current === 'browser') {
              barcodeDetectorRef.current.detect(rawVideoImageData).then((detector_result: any[]) => {
                if (detector_result.length !== 0) {
                  const first_result = detector_result[0]
                  const code = first_result.rawValue
                  webWorkerRef.current.postMessage({ 'action': 'reportCodeFound', 'code': code, 'imageData': rawVideoImageData })
                  webWorkerRef.current.postMessage({ 'action': 'findQRCode', 'imageData': rawVideoImageData })
                }
              })
            } else {
              webWorkerRef.current.postMessage({ 'action': 'findQRCode', 'imageData': rawVideoImageData })
            }
          }
        }
      }


      for (const canvasRef of canvasesToStreamToRef.current) {
        const canvas = canvasRef.current
        const ctx = canvas!.getContext("2d")
        if (ctx) {

          const canvas_width = canvas!.width
          const canvas_height = canvas!.height
          const video_aspect_ratio = video_width / video_height
          const canvas_aspect_ratio = canvas_width / canvas_height

          let render_width = canvas_width
          let render_height = canvas_width / video_aspect_ratio
          let cropped_video_width = video_width
          let cropped_video_height = video_height
          let x_adjust = 0
          let y_adjust = 0


          if (canvas_aspect_ratio < video_aspect_ratio) {
            render_height = canvas_height
            render_width = canvas_height * video_aspect_ratio
          }

          if (render_width > canvas_width) {
            const overflow_as_fraction = 1 - (canvas_width / render_width)
            cropped_video_width = video_width * (1 - overflow_as_fraction)
            x_adjust = (overflow_as_fraction / 2) * video_width
            render_width = canvas_width
          }
          if (render_height > canvas_height) {
            const overflow_as_fraction = 1 - (canvas_height / render_height)
            cropped_video_height = video_height * (1 - overflow_as_fraction)
            y_adjust = (overflow_as_fraction / 2) * video_height
            render_height = canvas_height
          }


          ctx.save();
          // apply zoom, keeping centred and mirrored if front camera
          ctx.translate(render_width / 2, render_height / 2);
          ctx.scale(tickZoom.current * (isFacingUserRef.current ? -1 : 1), tickZoom.current);
          ctx.translate(-render_width / 2, -render_height / 2);
          // this is the image cropped to the phone screen and window

          if (tickLongExposureMode.current) {
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.globalAlpha = beta;
            ctx.drawImage(videoElement, x_adjust, y_adjust, cropped_video_width, cropped_video_height, 0, 0, render_width, render_height);
            ctx.restore();
          } else {
            ctx.drawImage(videoElement, x_adjust, y_adjust, cropped_video_width, cropped_video_height, 0, 0, render_width, render_height)

          }

          ctx.restore();


          if (qr_search_options && qr_search_options['enabled'] && qr_search_options['searchArea'] && qr_search_options['showSearchArea']) {
            const scaleFactors = qr_search_options['searchArea']
            const searchArea = { top: 0, left: 0, width: 0, height: 0 }
            searchArea.top = Math.ceil(render_height * scaleFactors.top)
            searchArea.left = Math.ceil(render_width * scaleFactors.left)
            searchArea.width = Math.ceil(render_width * scaleFactors.width)
            searchArea.height = Math.ceil(render_height * scaleFactors.height)
            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'; // Red color with 50% opacity
            ctx.fillRect(0, 0, render_width, searchArea.top)
            ctx.fillRect(0, searchArea.height + searchArea.top, render_width, searchArea.height)
            ctx.fillRect(0, searchArea.top, searchArea.left, searchArea.height)
            ctx.fillRect(searchArea.left + searchArea.width, searchArea.top, (render_width - (searchArea.width + searchArea.left)), searchArea.height)

          }

        }
      }
    }
    animationFrameHandleRef.current = requestAnimationFrame(() => { tick(newFrameCount) })
  }, [])

  useEffect(() => {
    tick(0)
    return (() => { cancelAnimationFrame(animationFrameHandleRef.current) })
  }, [tick]);


  const getDomainFromURL = (urlString: string) => {
    if (urlString === "") {
      return ""
    }
    const url = new URL(urlString);
    const domain = url.hostname;
    return domain
  }

  const handleMessageFromWebWorker = (message: any) => {
    const messageData = message.data
    const action = messageData.action
    const qr_search_options = QRCodeSearchOptionsRef.current!
    switch (action) {
      case 'qrCodeFound':
        //     console.log(' QR code found')
        const code = messageData.code
        let domainValid = true
        if (qr_search_options && qr_search_options['expectedDomains']) {
          const qr_domain = getDomainFromURL(code.data)
          domainValid = false
          for (const validDomain of qr_search_options['expectedDomains']) {
            if (validDomain.toLowerCase() === qr_domain.toLowerCase()) {
              domainValid = true
            }
          }
        }
        if (domainValid && (cameraStateRef.current === 'RUNNING')) {
          setQRCodesPresent([messageData.code.data])
          frameSinceQRCodeSpotted.current = 0
          //  console.log('💚')
          //  console.log(JSON.stringify(qr_search_options))

        } else {
          console.log(`Ignoring QR Code because camera state is ${cameraState}`)
        }

        break;
      case 'noQRCodeFound':
        frameSinceQRCodeSpotted.current = frameSinceQRCodeSpotted.current + 1
        if (frameSinceQRCodeSpotted.current === 30) {
          console.log('no code found 💚')
          setQRCodesPresent(null)
        }
        break;

      default:
        console.log(`got unknown message from worker ${action}`)
        break;
    }
  }

  const _getAvailableDevices = async () => {
    if(!navigator || !navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
      return []
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');
    return videoDevices
  }


  const chooseCameraAndGetVideoConstraints = (videoDevices: MediaDeviceInfo[]) => {
    const screen_height = window.screen.height  //  window.innerHeight
    const screen_width = window.screen.width
    let window_width = window.innerWidth
    const screen_aspect_ratio = screen_width / screen_height
    //   const aspect_ratio_to_use = (screen_height > screen_width) ? 1 / screen_aspect_ratio : screen_aspect_ratio
    const aspect_ratio_to_use = 1
    window_width = window_width * 4
    const max_width = 1280
    if (window_width > max_width) {
      window_width = max_width
    }
    let deviceId = videoDevices[videoDevices.length - 1] && videoDevices[videoDevices.length - 1]['deviceId']
    if (['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform)) {
      if (videoDevices.length >= 2) {
        deviceId = videoDevices[1]['deviceId']
      }
    }

    const options: any = {
      width: { 'max': window_width, 'ideal': window_width },
      aspectRatio: { 'exact': aspect_ratio_to_use },
      frameRate: { 'max': 30, 'ideal': 30 }
    }

    const validDeviceIds = []
    for (const device of availableCameras) {
      validDeviceIds.push(device.deviceId)
    }
    const deviceFromLocalStorage = localStorage.getItem('selectedVideoInputDevice')
    if (deviceFromLocalStorage && validDeviceIds.includes(deviceFromLocalStorage)) {
      options['deviceId'] = { 'exact': deviceFromLocalStorage }
    } else {
      if (deviceId && deviceId !== '') {
        options['deviceId'] = { 'exact': deviceId }
      }
      options['facingMode'] = 'environment'
    }

    return options
  }

  const clearCameraStateVariables = () => {
    setQRCodesPresent(null)
  }

  const stopCamera = () => {
    if (cameraStateRef.current !== 'IDLE') {
      cameraStateRef.current = 'IDLE'
      setCameraState('IDLE')
      clearCameraStateVariables()
      const stream = mediaStreamRef.current
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  }

  const handleTrackEnded = (event: Event) => {
    //console.log(`🍍 🍍 🍍 🍍 🍍 Video track ended detected ${event}`)
    if (cameraState === 'RUNNING') {
      startCamera()
    }
  }

  const startCamera = async () => {
    // set state to starting as theres some async stuff going on and this funciton will return before the video has actually started
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    const myLockId = JSON.stringify(array)
    if (videoStartLockRef.current === null) {
      videoStartLockRef.current = myLockId
    } else {
      return
    }

    setCameraState('STARTING')
    clearCameraStateVariables()

    let videoConstraints = null
    // use get user media to get camera stream
    videoConstraints = chooseCameraAndGetVideoConstraints(availableCameras)
    //console.log("Selected video constraints: ", videoConstraints)
    setSelectedCamera(videoConstraints['deviceId'])
    // console.log("🎥 Trying to get camera with id ", videoConstraints['deviceId'])
    // console.log(videoConstraints)
    await navigator.mediaDevices.getUserMedia({ video: videoConstraints }).then(async function (stream) {
      if (videoStartLockRef.current !== myLockId) {
        return
      }
      const videoElement = videoElementRef.current
      mediaStreamRef.current = stream
      videoElement!.srcObject = stream
      videoElement!.setAttribute("playsinline", "true") // required to tell iOS safari we don't want fullscreen
      if (videoStartLockRef.current !== myLockId) {
        return
      }
      //console.log(`▶️ Starting video ${cameraState} ${Date.now()}`)
      const playPromise = videoElement!.play()
      if (playPromise !== undefined) {
        playPromise.then(_ => {
          setCameraState('RUNNING')
          cameraStateRef.current = 'RUNNING'
          videoStartLockRef.current = null
          let cameraDeviceId = null
          let facingUser = false
          stream.getTracks().forEach(track => {

            const capabilities = track.getCapabilities();
            const settings = track.getSettings();
            // console.log("Camera capabilities:", capabilities)
            // console.log("Camera settings:", settings)
            if ((capabilities && capabilities.facingMode && capabilities.facingMode.includes('user')) || (capabilities && capabilities.facingMode && capabilities.facingMode.length === 0)) {
              facingUser = true
            }
            cameraDeviceId = settings.deviceId
            //console.log("Got camera", cameraDeviceId)

            track.addEventListener('ended', (event) => { handleTrackEnded(event) });
            setSelectedCamera(`${cameraDeviceId}`)
          });
          if (facingUser) {
            setIsFacingUser(true)
          } else {
            setIsFacingUser(false)
          }
        })
      }

    }).catch(function (err) {
      console.error(`Error accessing camera: ${err}`)
      // if its a NotAllowedError, the user has denied access to the camera
      // we need to set a more specific error state here
      // if NotFoundError then the device has no camera
      // if NotReadableError then the camera may be already in use or may be broken

      if (err.name === 'NotAllowedError') {
        setCameraState('PERMISSION_DENIED')
        cameraStateRef.current = 'PERMISSION_DENIED'
      } else if (err.name === 'NotFoundError') {
        setCameraState('NO_CAMERA_FOUND')
        cameraStateRef.current = 'NO_CAMERA_FOUND'
      } else if (err.name === 'NotReadableError') {
        setCameraState('CAMERA_UNREADABLE')
        cameraStateRef.current = 'CAMERA_UNREADABLE'
      } else {
        setCameraState('ERROR')
        cameraStateRef.current = 'ERROR'
      }
      videoStartLockRef.current = null
    })

  }

  const streamToMyCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
    //console.log('✅ STARTING THE CAMERA')
    if (canvasesToStreamToRef.current.length === 0) {
      startCamera();
    }
    if (canvasesToStreamToRef.current.indexOf(canvasRef) === -1) {
      canvasesToStreamToRef.current.push(canvasRef)
    }
  }

  const stopStreamingToMyCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
    //console.log('⛔️ STOPPING THE CAMERA')
    canvasesToStreamToRef.current = canvasesToStreamToRef.current.filter(function (e) { return e !== canvasRef })
    if (canvasesToStreamToRef.current.length === 0) {
      stopCamera()
    }
  }

  const selectCamera = (cameraId: string) => {
    console.log("Changing camera")
    if (cameraId !== selectedCamera) {
      localStorage.setItem('selectedVideoInputDevice', cameraId)
      if (cameraState === 'RUNNING') {
        setCameraState('SWITCHING')
        stopCamera()
        startCamera()
        setCameraState('RUNNING')
      }
    }
  }


  const captureAsJPEGString = (width: number | undefined = undefined, orientation: string = 'unknown') => {
    console.log("Capturing image as JPEG string with width ", width)
    console.log('camera state is ', cameraStateRef.current)

    // if the camera state is permission denied or no camera found write an error message to the canvas as text
    if (cameraStateRef.current === 'PERMISSION_DENIED' || cameraStateRef.current === 'NO_CAMERA_FOUND' || cameraStateRef.current === 'CAMERA_UNREADABLE' || cameraStateRef.current === 'ERROR') {
      const font_size = 40
      const captureCanvas = captureCanvasRef.current
      const ctx = captureCanvas!.getContext("2d")
      const canvas_width = 640
      const canvas_height = 480
      captureCanvas!.width = canvas_width
      captureCanvas!.height = canvas_height
      ctx!.fillStyle = 'black'
      ctx!.fillRect(0, 0, canvas_width, canvas_height)
      ctx!.fillStyle = 'white'
      ctx!.font = `${font_size}px Arial`
      ctx!.textAlign = 'center'
      const cameraStateCode = cameraStateRef.current ?? 'UNKNOWN'
      let errorMessage = ''
      if (cameraStateCode === 'PERMISSION_DENIED') {
        errorMessage = t('SharedComponents.VideoProvider.cameraErrorPermissionDenied')
      } else if (cameraStateCode === 'NO_CAMERA_FOUND') {
        errorMessage = t('SharedComponents.VideoProvider.cameraErrorNoCameraFound')
      } else if (cameraStateCode === 'CAMERA_UNREADABLE') {
        errorMessage = t('SharedComponents.VideoProvider.cameraErrorCameraUnreadable')
      } else {
        errorMessage = t('SharedComponents.VideoProvider.cameraErrorUnexpected')
      }
      errorMessage = `⚠️ ${errorMessage}`
      const lineHeight = font_size * 1.2
      const lines = []
      let currentLine = ''
      const words = errorMessage.split(' ')
      for (const word of words) {
        const testLine = currentLine + word + ' '
        const testWidth = ctx!.measureText(testLine).width
        if (testWidth > canvas_width - 40) {
          lines.push(currentLine)
          currentLine = word + ' '
        } else {
          currentLine = testLine
        }
      }
      lines.push(currentLine)
      // + 1 line to leave room for the raw camera state code drawn underneath
      const totalTextHeight = (lines.length + 1) * lineHeight
      let startY = (canvas_height - totalTextHeight) / 2 + lineHeight / 2
      for (const line of lines) {
        ctx!.fillText(line, canvas_width / 2, startY)
        startY += lineHeight
      }

      // show the raw camera state code beneath the localised message so anyone auditing
      // who doesn't speak the translated language can still identify the failure
      ctx!.fillStyle = '#9ca3af'
      ctx!.font = `${font_size * 0.6}px monospace`
      ctx!.fillText(cameraStateCode, canvas_width / 2, startY + lineHeight * 0.4)

      // orientation tells us which side of the image the floor is on,  it should be at the bottom
      // so if its on the left, the right, or the top, we need to rotate the image so the orientation is correct
      // the image is always square so we can just rotate it 90 degrees if the orientation is left or right, and 180 degrees if its top

      const imageString = captureCanvas?.toDataURL('image/jpeg')
      return imageString
    }

    const rawVideoCanvas = rawVideoCanvasRef.current
    const captureCanvas = captureCanvasRef.current
    if (!rawVideoCanvas) {
      return undefined
    }
    if (width === undefined) {
      const imageString = rawVideoCanvas?.toDataURL('image/jpeg')
      return imageString
    } else {
      let scale_factor = width / rawVideoCanvas.width
      if (scale_factor > 1) {
        captureCanvas!.width = rawVideoCanvas.width
        captureCanvas!.height = rawVideoCanvas.height
      } else {
        captureCanvas!.width = width
        captureCanvas!.height = (rawVideoCanvas.height * scale_factor)
      }
      const captureCanvasCtx = captureCanvas?.getContext("2d")

      captureCanvasCtx!.drawImage(rawVideoCanvas, 0, 0, rawVideoCanvas.width, rawVideoCanvas.height, 0, 0, captureCanvas!.width, captureCanvas!.height)
      const canvas_width = captureCanvas!.width
      const canvas_height = captureCanvas!.height
      if (!rotatedCanvasRef.current) {
        console.error('No rotated canvas found in ref')
        return ""
      }
      if (orientation === 'left') {

        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_height
        rotatedCanvas.height = canvas_width
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate(90 * Math.PI / 180)
        rotatedCtx!.translate(-rotatedCanvas.height / 2, -rotatedCanvas.width / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      } else if (orientation === 'right') {

        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_height
        rotatedCanvas.height = canvas_width
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate((270 * Math.PI) / 180)
        rotatedCtx!.translate(-rotatedCanvas.height / 2, -rotatedCanvas.width / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      } else if (orientation === 'top') {
        const rotatedCanvas = rotatedCanvasRef.current
        rotatedCanvas.width = canvas_width
        rotatedCanvas.height = canvas_height
        const rotatedCtx = rotatedCanvas.getContext('2d')
        rotatedCtx!.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
        rotatedCtx!.rotate(180 * Math.PI / 180)
        rotatedCtx!.translate(-rotatedCanvas.width / 2, -rotatedCanvas.height / 2)
        rotatedCtx!.drawImage(captureCanvas!, 0, 0)
        return rotatedCanvas.toDataURL('image/jpeg')
      }



      const imageString = captureCanvas?.toDataURL('image/jpeg')

      console.log(`
    scaling captured image from
    ${rawVideoCanvas.width}x${rawVideoCanvas.height} (${rawVideoCanvas.width / rawVideoCanvas.height})
    to:
    ${captureCanvas!.width}x${captureCanvas!.height} (${captureCanvas!.width / captureCanvas!.height}`)

      return imageString
    }
  }

  return (
    <VideoContext.Provider value={{
      stopStreamingToMyCanvas,
      streamToMyCanvas,
      availableCameras,
      selectCamera,
      selectedCamera,
      cameraState,
      QRCodesPresent,
      setQRCodesPresent,
      setQRCodesSearchOptions,
      captureAsJPEGString,
      clearCameraStateVariables,
      zoom,
      setZoom,
      setLongExposureMode,
      longExposureMode
    }}>
      <div className={'hidden'}>
        <video
          ref={videoElementRef}
          autoPlay={true}
          muted={true}
          playsInline={true}
        // style={{ outline: '2px solid red' }}
        ></video>
        <canvas ref={rawVideoCanvasRef}></canvas>
        <canvas ref={captureCanvasRef}></canvas>
        <canvas ref={rotatedCanvasRef}></canvas>

      </div>

      {children}

    </VideoContext.Provider>
  );
};

export default VideoProvider;
